import { driver, Driver, PopoverDOM } from "driver.js";
import i18next from "i18next";
import { ensureDmSansFont } from "../core/ui-kit/designTokens";
import { logger } from "../util/Logger";
import { TipStorage } from "./TipStorage";
import { ContextualTip, getContextualTip } from "./scenarios/ContextualTips";
import {
  cleanupOnboardingUi,
  getOnboardingSteps,
  isStartModalOpen,
  type OnboardingStartAt,
} from "./scenarios/OnboardingTour";
import { getProgressTip, ProgressTip } from "./scenarios/ProgressTips";

const POPOVER_CLASS = "sugar-tip-popover";

const DRIVER_BASE_CONFIG = {
  animate: true,
  overlayColor: "#0f172a",
  overlayOpacity: 0.45,
  stagePadding: 8,
  stageRadius: 12,
  popoverClass: POPOVER_CLASS,
  showProgress: true,
  showButtons: ["close" as const, "next" as const],
  allowClose: true,
  overlayClickBehavior: "nextStep" as const,
};

const DRIVER_CSS = `.driver-active .driver-overlay,.driver-active *{pointer-events:none}.driver-active .driver-active-element,.driver-active .driver-active-element *,.driver-popover,.driver-popover *{pointer-events:auto}@keyframes animate-fade-in{0%{opacity:0}to{opacity:1}}.driver-fade .driver-overlay{animation:animate-fade-in .2s ease-in-out}.driver-fade .driver-popover{animation:animate-fade-in .2s}.driver-popover{all:unset;box-sizing:border-box;color:#2d2d2d;margin:0;padding:15px;border-radius:5px;min-width:250px;max-width:300px;box-shadow:0 1px 10px #0006;z-index:1000000000;position:fixed;top:0;right:0;background-color:#fff}.driver-popover *{font-family:Helvetica Neue,Inter,ui-sans-serif,"Apple Color Emoji",Helvetica,Arial,sans-serif}.driver-popover-title{font:19px/normal sans-serif;font-weight:700;display:block;position:relative;line-height:1.5;zoom:1;margin:0}.driver-popover-close-btn{all:unset;position:absolute;top:0;right:0;width:32px;height:28px;cursor:pointer;font-size:18px;font-weight:500;color:#d2d2d2;z-index:1;text-align:center;transition:color;transition-duration:.2s}.driver-popover-close-btn:hover,.driver-popover-close-btn:focus{color:#2d2d2d}.driver-popover-title[style*=block]+.driver-popover-description{margin-top:5px}.driver-popover-description{margin-bottom:0;font:14px/normal sans-serif;line-height:1.5;font-weight:400;zoom:1}.driver-popover-footer{margin-top:15px;text-align:right;zoom:1;display:flex;align-items:center;justify-content:space-between}.driver-popover-progress-text{font-size:13px;font-weight:400;color:#727272;zoom:1}.driver-popover-footer button{all:unset;display:inline-block;box-sizing:border-box;padding:3px 7px;text-decoration:none;text-shadow:1px 1px 0 #fff;background-color:#fff;color:#2d2d2d;font:12px/normal sans-serif;cursor:pointer;outline:0;zoom:1;line-height:1.3;border:1px solid #ccc;border-radius:3px}.driver-popover-footer .driver-popover-btn-disabled{opacity:.5;pointer-events:none}:not(body):has(>.driver-active-element){overflow:hidden!important}.driver-no-interaction,.driver-no-interaction *{pointer-events:none!important}.driver-popover-footer button:hover,.driver-popover-footer button:focus{background-color:#f7f7f7}.driver-popover-navigation-btns{display:flex;flex-grow:1;justify-content:flex-end}.driver-popover-navigation-btns button+button{margin-left:4px}.driver-popover-arrow{content:"";position:absolute;border:5px solid #fff}.driver-popover-arrow-side-over{display:none}.driver-popover-arrow-side-left{left:100%;border-right-color:transparent;border-bottom-color:transparent;border-top-color:transparent}.driver-popover-arrow-side-right{right:100%;border-left-color:transparent;border-bottom-color:transparent;border-top-color:transparent}.driver-popover-arrow-side-top{top:100%;border-right-color:transparent;border-bottom-color:transparent;border-left-color:transparent}.driver-popover-arrow-side-bottom{bottom:100%;border-left-color:transparent;border-top-color:transparent;border-right-color:transparent}.driver-popover-arrow-side-center{display:none}.driver-popover-arrow-side-left.driver-popover-arrow-align-start,.driver-popover-arrow-side-right.driver-popover-arrow-align-start{top:15px}.driver-popover-arrow-side-top.driver-popover-arrow-align-start,.driver-popover-arrow-side-bottom.driver-popover-arrow-align-start{left:15px}.driver-popover-arrow-align-end.driver-popover-arrow-side-left,.driver-popover-arrow-align-end.driver-popover-arrow-side-right{bottom:15px}.driver-popover-arrow-side-top.driver-popover-arrow-align-end,.driver-popover-arrow-side-bottom.driver-popover-arrow-align-end{right:15px}.driver-popover-arrow-side-left.driver-popover-arrow-align-center,.driver-popover-arrow-side-right.driver-popover-arrow-align-center{top:50%;margin-top:-5px}.driver-popover-arrow-side-top.driver-popover-arrow-align-center,.driver-popover-arrow-side-bottom.driver-popover-arrow-align-center{left:50%;margin-left:-5px}.driver-popover-arrow-none{display:none}`;

const SUGAR_THEME_CSS = `
/* Overlay SVG is z-index 10000. Chrome lives inside left-pane (z-index 30),
   so the cutout alone still shows a dimmed/frosted control. Elevate the host
   that contains the active target above the overlay so it is fully visible. */
body.driver-active left-pane:has(.driver-active-element),
body.driver-active right-pane:has(.driver-active-element) {
  z-index: 10050 !important;
}

/* sugar-request-modal is z-index 100000 — keep Teklif Al icon above its backdrop. */
body.driver-active left-pane:has(#button-quote.driver-active-element) {
  z-index: 100150 !important;
}

/* Keep start-modal actions clickable above the tip overlay. */
body.driver-active left-pane:has(#room-shaping-start-actions.driver-active-element),
body.driver-active left-pane:has(.room-shaping-actions.driver-active-element) {
  z-index: 100150 !important;
}

body.driver-active left-pane:has(.driver-active-element) .top-chrome-bar {
  /* backdrop-filter samples layers under the bar and keeps the control muddy */
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  background: #ffffff !important;
}

body.driver-active .driver-active-element {
  background-color: #ffffff !important;
  box-shadow:
    0 0 0 3px #ffffff,
    0 0 0 7px rgba(159, 184, 169, 1),
    0 10px 28px rgba(15, 23, 42, 0.22) !important;
  border-radius: 12px;
  outline: none;
}

/* Welcome tip: ring around Sıfırdan çiz + Şablonu Seç without washing out buttons. */
body.driver-active #room-shaping-start-actions.driver-active-element {
  background-color: transparent !important;
  padding: 8px;
  gap: 12px;
  box-shadow:
    0 0 0 3px #ffffff,
    0 0 0 7px rgba(159, 184, 169, 1),
    0 10px 28px rgba(15, 23, 42, 0.22) !important;
}

body.driver-active #view-mode-toggle.driver-active-element,
body.driver-active #view-mode-toggle-mobile.driver-active-element {
  background-color: var(--srd-surface-solid, #ffffff) !important;
}

.sugar-tip-popover.driver-popover {
  --srd-font: "DM Sans", sans-serif;
  --srd-text: #0f172a;
  --srd-text-muted: #64748b;
  --srd-border: rgba(15, 23, 42, 0.08);
  --srd-border-strong: rgba(15, 23, 42, 0.14);
  --srd-bg: #f3f4f6;
  --srd-bg-muted: #e8eaed;
  --srd-button: #9fb8a9;
  --srd-button-hover: #8fa698;
  --srd-button-text: #1f2937;
  --srd-focus-ring: 0 0 0 2px #ffffff, 0 0 0 4px rgba(159, 184, 169, 0.22);

  all: unset;
  box-sizing: border-box;
  position: fixed;
  z-index: 1000000000;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: min(100vw - 32px, 280px);
  max-width: min(100vw - 32px, 400px);
  margin: 0;
  padding: 24px;
  border: 1px solid var(--srd-border);
  border-radius: 16px;
  background: #ffffff;
  color: var(--srd-text);
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.12);
  font-family: var(--srd-font);
  -webkit-font-smoothing: antialiased;
  animation: sugar-tip-enter 180ms ease-out;
}

@keyframes sugar-tip-enter {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.sugar-tip-popover.driver-popover * {
  font-family: var(--srd-font);
}

.sugar-tip-popover .driver-popover-title {
  display: block;
  margin: 0;
  padding-right: 44px;
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.35;
  letter-spacing: -0.02em;
  color: var(--srd-text);
}

.sugar-tip-popover .driver-popover-title[style*="block"] + .driver-popover-description {
  margin-top: 8px;
}

.sugar-tip-popover .driver-popover-description {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 400;
  line-height: 1.55;
  color: var(--srd-text-muted);
}

.sugar-tip-popover .driver-popover-close-btn {
  all: unset;
  box-sizing: border-box;
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  display: flex !important;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  color: var(--srd-text);
  font-size: 1.75rem;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  pointer-events: auto;
  transition:
    background-color 160ms ease-out,
    color 160ms ease-out;
}

.sugar-tip-popover .driver-popover-close-btn:hover,
.sugar-tip-popover .driver-popover-close-btn:focus-visible {
  color: var(--srd-text);
  background: var(--srd-bg);
  outline: none;
}

.sugar-tip-popover .driver-popover-footer {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  margin-top: 0;
  text-align: initial;
}

.sugar-tip-popover .driver-popover-progress-text {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.3;
  color: var(--srd-text-muted);
  text-align: center;
}

.sugar-tip-popover .driver-popover-navigation-btns {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  flex-grow: 0;
  justify-content: stretch;
  width: 100%;
}

.sugar-tip-popover .driver-popover-navigation-btns:has(.sugar-tip-dont-show-btn) {
  grid-template-columns: 1fr 1fr;
}

.sugar-tip-popover .driver-popover-navigation-btns button,
.sugar-tip-popover .driver-popover-footer > button {
  all: unset;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 44px;
  padding: 10px 14px;
  border: none;
  border-radius: 12px;
  background: var(--srd-button);
  color: var(--srd-button-text);
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  text-shadow: none;
  cursor: pointer;
  transition:
    background-color 160ms ease-out,
    transform 160ms ease-out,
    box-shadow 160ms ease-out;
}

.sugar-tip-popover .driver-popover-navigation-btns .sugar-tip-dont-show-btn {
  background: var(--srd-bg-muted);
  color: var(--srd-text);
  border: 1px solid var(--srd-border-strong);
}

.sugar-tip-popover .driver-popover-navigation-btns button:hover,
.sugar-tip-popover .driver-popover-navigation-btns button:focus-visible,
.sugar-tip-popover .driver-popover-footer > button:hover,
.sugar-tip-popover .driver-popover-footer > button:focus-visible {
  background: var(--srd-button-hover);
  outline: none;
  box-shadow: var(--srd-focus-ring);
}

.sugar-tip-popover .driver-popover-navigation-btns .sugar-tip-dont-show-btn:hover,
.sugar-tip-popover .driver-popover-navigation-btns .sugar-tip-dont-show-btn:focus-visible {
  background: var(--srd-bg);
  outline: none;
  box-shadow: var(--srd-focus-ring);
}

.sugar-tip-popover .driver-popover-navigation-btns button:active,
.sugar-tip-popover .driver-popover-footer > button:active {
  transform: scale(0.98);
}

.sugar-tip-popover .driver-popover-navigation-btns button + button {
  margin-left: 0;
}

.sugar-tip-popover .driver-popover-footer .driver-popover-btn-disabled,
.sugar-tip-popover .driver-popover-navigation-btns .driver-popover-btn-disabled {
  opacity: 0.55;
  pointer-events: none;
}

.sugar-tip-popover .driver-popover-arrow {
  border-color: #ffffff;
}

@media (max-width: 480px) {
  .sugar-tip-popover.driver-popover {
    min-width: min(100vw - 24px, 280px);
    max-width: min(100vw - 24px, 400px);
    padding: 20px 16px;
    gap: 16px;
    animation: sugar-tip-enter-mobile 200ms ease-out;
  }

  @keyframes sugar-tip-enter-mobile {
    from {
      opacity: 0;
      transform: translateY(16px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .sugar-tip-popover .driver-popover-title {
    font-size: 1.0625rem;
  }

  .sugar-tip-popover .driver-popover-description {
    font-size: 0.875rem;
  }

  .sugar-tip-popover .driver-popover-navigation-btns button,
  .sugar-tip-popover .driver-popover-footer > button {
    min-height: 48px;
    font-size: 0.9375rem;
    padding: 12px 16px;
  }

  .sugar-tip-popover .driver-popover-navigation-btns:has(.sugar-tip-dont-show-btn) {
    grid-template-columns: 1fr;
  }

  .sugar-tip-popover .driver-popover-close-btn {
    width: 44px;
    height: 44px;
    font-size: 2rem;
  }
}
`;

export class TipManager {
  private static _instance: TipManager | null = null;
  private static _cssInjected = false;
  private _rootElement: HTMLElement | null = null;
  private _driverInstance: Driver | null = null;
  private _hasEnteredThreeD = false;
  private _hasAddedProduct = false;
  private _autoDismissTimer: ReturnType<typeof setTimeout> | null = null;
  private _pendingTipTimers = new Set<ReturnType<typeof setTimeout>>();
  /** Waiting to start first-visit tour together with the template start modal. */
  private _pendingFirstVisitTour = false;
  private _firstVisitPollTimer: ReturnType<typeof setInterval> | null = null;
  private _firstVisitFallbackTimer: ReturnType<typeof setTimeout> | null = null;
  /** Soft-dismiss welcome tip without closing the template start page. */
  private _softDismissWelcome = false;
  private _resumeFromTemplates = false;
  private _startModalCloseWatch: ReturnType<typeof setInterval> | null = null;

  private constructor() {}

  static getInstance(): TipManager {
    if (!TipManager._instance) {
      TipManager._instance = new TipManager();
    }
    return TipManager._instance;
  }

  /** Tips/help feature is on (Settings → Usage tips). */
  isTipsEnabled(): boolean {
    return TipStorage.isTipsEnabled();
  }

  /**
   * Dismiss any active tip/tour and cancel pending contextual tip timers.
   * Used when the user disables tips or closes help.
   */
  stopAllTips(): void {
    this._pendingFirstVisitTour = false;
    this._softDismissWelcome = false;
    this._resumeFromTemplates = false;
    this._clearFirstVisitWaiters();
    this._clearStartModalCloseWatch();
    this._clearPendingTipTimers();
    this._clearAutoDismiss();
    cleanupOnboardingUi(this._rootElement);
    this._destroyActive();
  }

  /**
   * Close the welcome tip only. Keep the template start page open, then continue
   * the tour from the Şablonlar toolbar tip once that page is dismissed.
   */
  dismissWelcomeTipKeepStartModal(): void {
    if (!this._driverInstance) return;
    this._softDismissWelcome = true;
    this._resumeFromTemplates = true;
    this._destroyActive();
  }

  private _clearStartModalCloseWatch(): void {
    if (this._startModalCloseWatch) {
      clearInterval(this._startModalCloseWatch);
      this._startModalCloseWatch = null;
    }
  }

  private _watchStartModalThenResumeTemplates(): void {
    this._clearStartModalCloseWatch();
    const tryResume = () => {
      if (!this._resumeFromTemplates || !this.isTipsEnabled()) {
        this._clearStartModalCloseWatch();
        return;
      }
      if (isStartModalOpen(this._rootElement)) return;
      this._resumeFromTemplates = false;
      this._clearStartModalCloseWatch();
      this.startOnboardingTour({ startAt: "templates" });
    };
    tryResume();
    this._startModalCloseWatch = setInterval(tryResume, 200);
  }

  /** Turn off usage tips (Settings toggle / don't-show-again) and dismiss anything showing. */
  disableTips(): void {
    TipStorage.setTipsEnabled(false);
    TipStorage.markTourSeen("onboarding");
    TipStorage.setOnboardingReplay(false);
    this.stopAllTips();
  }

  private _handleCloseClick = (): void => {
    this._clearAutoDismiss();
    // Tip 1 (welcome) must never tear down the template start popup.
    if (this._isWelcomeTipActive() || isStartModalOpen(this._rootElement)) {
      this.dismissWelcomeTipKeepStartModal();
      return;
    }
    this._softDismissWelcome = false;
    this._resumeFromTemplates = false;
    this._clearStartModalCloseWatch();
    cleanupOnboardingUi(this._rootElement);
    this._driverInstance?.destroy();
    this._driverInstance = null;
  };

  private _isWelcomeTipActive(): boolean {
    const title = this._driverInstance?.getActiveStep()?.popover?.title;
    if (!title) return false;
    return (
      title === i18next.t("tips.onboarding.welcomeTitle") ||
      Boolean(
        this._rootElement?.querySelector(
          "#room-shaping-start-actions.driver-active-element",
        ),
      )
    );
  }

  private _injectDontShowAgainButton = (popover: PopoverDOM): void => {
    if (popover.footerButtons.querySelector(".sugar-tip-dont-show-btn")) return;

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "sugar-tip-dont-show-btn";
    btn.textContent = i18next.t("tips.dontShowAgain");
    btn.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      // Disable tips but keep the template start popup if tip 1 is showing.
      if (this._isWelcomeTipActive() || isStartModalOpen(this._rootElement)) {
        TipStorage.setTipsEnabled(false);
        TipStorage.markTourSeen("onboarding");
        TipStorage.setOnboardingReplay(false);
        this._resumeFromTemplates = false;
        this._softDismissWelcome = true;
        this._clearPendingTipTimers();
        this._clearAutoDismiss();
        this._clearFirstVisitWaiters();
        this._clearStartModalCloseWatch();
        this._destroyActive();
        return;
      }
      this.disableTips();
    });

    const nextBtn = popover.nextButton;
    if (nextBtn?.parentElement === popover.footerButtons) {
      popover.footerButtons.insertBefore(btn, nextBtn);
    } else {
      popover.footerButtons.appendChild(btn);
    }
  };

  private static _injectCSS(): void {
    ensureDmSansFont();
    const cssText = DRIVER_CSS + SUGAR_THEME_CSS;
    let style = document.head.querySelector(
      "style[data-sugar-tips]",
    ) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement("style");
      style.setAttribute("data-sugar-tips", "");
      document.head.appendChild(style);
    }
    style.textContent = cssText;
    TipManager._cssInjected = true;
  }

  init(
    rootElement: HTMLElement,
    options?: { waitForStartModal?: boolean },
  ): void {
    this._rootElement = rootElement;
    TipManager._injectCSS();

    logger.info("[TipManager] init called, rootElement:", rootElement.tagName, rootElement);
    logger.info("[TipManager] rootElement.shadowRoot:", rootElement.shadowRoot);
    logger.info("[TipManager] tipsEnabled:", TipStorage.isTipsEnabled());
    logger.info("[TipManager] hasSeenOnboarding:", TipStorage.hasSeenTour("onboarding"));
    logger.info("[TipManager] onboardingReplay:", TipStorage.isOnboardingReplay());

    if (!this.isTipsEnabled()) return;

    // First visit, or replay after "restart tour", until "don't show again".
    if (TipStorage.shouldAutoStartOnboarding()) {
      this._pendingFirstVisitTour = true;
      if (options?.waitForStartModal !== false) {
        this._armFirstVisitTourWait();
      } else {
        this._scheduleTip(800, () => this._beginFirstVisitTour());
      }
      // Modal may already be open (welcome-menu raced ahead of tip init).
      this._tryStartFirstVisitTour();
    }
  }

  /**
   * Called when the room-start / template modal has been opened and rendered.
   * Starts the first-visit tip tour in sync with that page.
   */
  onStartModalReady(): void {
    if (!this._pendingFirstVisitTour) return;
    this._tryStartFirstVisitTour();
  }

  private _armFirstVisitTourWait(): void {
    this._clearFirstVisitWaiters();
    this._firstVisitPollTimer = setInterval(() => {
      this._tryStartFirstVisitTour();
    }, 80);
    // Fallback if modal never appears (no welcome-menu) — start tips anyway.
    this._firstVisitFallbackTimer = setTimeout(() => {
      if (this._pendingFirstVisitTour) {
        logger.info(
          "[TipManager] First-visit start modal wait timed out; starting tour",
        );
        this._beginFirstVisitTour();
      }
    }, 6000);
  }

  private _clearFirstVisitWaiters(): void {
    if (this._firstVisitPollTimer) {
      clearInterval(this._firstVisitPollTimer);
      this._firstVisitPollTimer = null;
    }
    if (this._firstVisitFallbackTimer) {
      clearTimeout(this._firstVisitFallbackTimer);
      this._firstVisitFallbackTimer = null;
    }
  }

  private _hasStartModalActions(): boolean {
    const root = this._rootElement ?? document;
    return Boolean(
      root.querySelector("#draw-from-scratch-button") &&
        root.querySelector("#confirm-shaping-button") &&
        root.querySelector("#room-shaping-start-actions"),
    );
  }

  private _tryStartFirstVisitTour(): void {
    if (!this._pendingFirstVisitTour || !this.isTipsEnabled()) return;
    if (!this._hasStartModalActions()) return;
    this._beginFirstVisitTour();
  }

  private _beginFirstVisitTour(): void {
    if (!this._pendingFirstVisitTour) return;
    this._pendingFirstVisitTour = false;
    this._clearFirstVisitWaiters();
    this._debugAllSelectors();
    // One frame so the start modal finishes layout before driver measures it.
    requestAnimationFrame(() => {
      this.startOnboardingTour();
    });
  }

  private _debugAllSelectors(): void {
    const selectors = [
      "#designer-canvas",
      "#designer-tools-bar",
      "#view-mode-toggle",
      "#button-products",
      "#button-templates",
      "#button-feat",
      "#button-fullscreen",
      "#button-draw-wall",
      "#button-window",
      "#button-door",
      "#top-chrome-history",
      "#top-chrome-zoom-group",
      "#button-quote",
    ];

    console.group("[TipManager] Element Debug Report");
    logger.info("rootElement:", this._rootElement?.tagName);

    for (const sel of selectors) {
      const inRoot = this._rootElement?.querySelector(sel);
      const inDoc = document.querySelector(sel);
      const status = inRoot ? "ROOT" : inDoc ? "DOCUMENT" : "NOT FOUND";
      logger.info(
        `  ${status} | "${sel}" | root:`, inRoot,
        "| document:", inDoc
      );
    }

    logger.info("--- rootElement children ---");
    if (this._rootElement) {
      logger.info("  childElementCount:", this._rootElement.childElementCount);
      logger.info("  innerHTML preview:", this._rootElement.innerHTML.substring(0, 500));
      logger.info("  all child tags:", Array.from(this._rootElement.querySelectorAll("*")).slice(0, 30).map(e => e.tagName + (e.id ? `#${e.id}` : "") + (e.className ? `.${String(e.className).split(" ").join(".")}` : "")));
    }

    console.groupEnd();
  }

  private _advanceOrDestroy(): void {
    if (!this._driverInstance) return;
    if (this._driverInstance.hasNextStep()) {
      this._driverInstance.moveNext();
    } else {
      this._driverInstance.destroy();
    }
  }

  startOnboardingTour(options?: { startAt?: OnboardingStartAt }): void {
    this._destroyActive();

    if (!this.isTipsEnabled()) return;

    if (!this._rootElement) {
      logger.warn("[TipManager] Cannot start tour - no rootElement");
      return;
    }

    const startAt = options?.startAt ?? "welcome";
    const steps = getOnboardingSteps(this._rootElement, {
      startAt,
      onDismissWelcomeKeepStartModal: () =>
        this.dismissWelcomeTipKeepStartModal(),
    });
    logger.info(
      "[TipManager] Starting onboarding tour with",
      steps.length,
      "steps; startAt=",
      startAt,
    );

    this._driverInstance = driver({
      ...DRIVER_BASE_CONFIG,
      showButtons: ["next", "close"],
      doneBtnText: i18next.t("tips.doneBtnText"),
      steps,
      onCloseClick: this._handleCloseClick,
      onPopoverRender: this._injectDontShowAgainButton,
      onHighlightStarted: (element, step) => {
        logger.info(
          `[TipManager] Step highlight | element:`, element,
          "| title:", step.popover?.title
        );
      },
      onDestroyed: () => {
        this._driverInstance = null;
        if (this._softDismissWelcome) {
          const shouldResume = this._resumeFromTemplates;
          this._softDismissWelcome = false;
          logger.info(
            "[TipManager] Welcome tip soft-dismissed; keeping start modal",
          );
          // Never close the template popup here — only resume later if requested.
          if (shouldResume) {
            this._watchStartModalThenResumeTemplates();
          }
          return;
        }
        logger.info("[TipManager] Onboarding tour destroyed/completed");
        TipStorage.markTourSeen("onboarding");
        // Keep start modal if user is still on the first-visit template page.
        cleanupOnboardingUi(this._rootElement, {
          keepStartModal: isStartModalOpen(this._rootElement),
        });
      },
    });
    this._driverInstance.drive();
  }

  restartTour(): void {
    TipStorage.resetAll();
    TipStorage.setTipsEnabled(true);
    // Keep autoplaying on later visits until the user picks "don't show again".
    TipStorage.setOnboardingReplay(true);
    this._hasEnteredThreeD = false;
    this._hasAddedProduct = false;
    this._pendingFirstVisitTour = false;
    this._softDismissWelcome = false;
    this._resumeFromTemplates = false;
    this._clearFirstVisitWaiters();
    this._clearStartModalCloseWatch();
    this.startOnboardingTour();
  }

  showContextualTip(tipId: string): void {
    if (!this.isTipsEnabled()) return;
    if (TipStorage.hasSeenTip(tipId)) return;

    const tipDef: ContextualTip | undefined = getContextualTip(tipId);
    if (!tipDef) return;

    const el = this._findElement(tipDef.element);
    //console.log ( ` [TipManager] showContextualTip(" ${tipId} ") | selector=" ${tipDef.element} " | found: ` , el );

    if (!el) {
      logger.warn(`[TipManager] Contextual tip skipped - element not found: "${tipDef.element}"`);
      return;
    }

    this._destroyActive();
    this._clearAutoDismiss();

    this._driverInstance = driver({
      ...DRIVER_BASE_CONFIG,
      showButtons: ["next", "close"],
      doneBtnText: i18next.t("tips.doneBtnText"),
      overlayClickBehavior: "close",
      steps: [
        {
          element: el,
          popover: {
            title: tipDef.title,
            description: tipDef.description,
            side: tipDef.side,
            align: tipDef.align,
          },
        },
      ],
      onCloseClick: this._handleCloseClick,
      onPopoverRender: this._injectDontShowAgainButton,
      onDestroyed: () => {
        TipStorage.markTipSeen(tipId);
        this._clearAutoDismiss();
        this._driverInstance = null;
      },
    });
    this._driverInstance.drive();

    this._autoDismissTimer = setTimeout(() => {
      this._destroyActive();
    }, tipDef.autoDismissMs);
  }

  showProgressTip(tipId: string): void {
    if (!this.isTipsEnabled()) return;
    if (TipStorage.hasSeenTip(tipId)) return;

    const tipDef: ProgressTip | undefined = getProgressTip(tipId);
    if (!tipDef) return;

    const el = this._findElement(tipDef.element);
    //console.log(`[TipManager] showProgressTip("${tipId}") | selector="${tipDef.element}" | found:`, el);
    if (!el) {
      logger.warn(`[TipManager] Progress tip skipped - element not found: "${tipDef.element}"`);
      return;
    }

    this._destroyActive();
    this._clearAutoDismiss();

    this._driverInstance = driver({
      ...DRIVER_BASE_CONFIG,
      showButtons: ["next", "close"],
      doneBtnText: i18next.t("tips.doneBtnText"),
      overlayClickBehavior: "close",
      steps: [
        {
          element: el,
          popover: {
            title: tipDef.title,
            description: tipDef.description,
            side: tipDef.side,
            align: tipDef.align,
          },
        },
      ],
      onCloseClick: this._handleCloseClick,
      onPopoverRender: this._injectDontShowAgainButton,
      onDestroyed: () => {
        TipStorage.markTipSeen(tipId);
        this._clearAutoDismiss();
        this._driverInstance = null;
      },
    });
    this._driverInstance.drive();

    this._autoDismissTimer = setTimeout(() => {
      this._destroyActive();
    }, tipDef.autoDismissMs);
  }

  // --- Event Handlers (called from DesignMenu) ---

  onRenderModeChanged(mode: string): void {
    if (!this.isTipsEnabled()) return;
    if (mode === "3d") {
      if (!this._hasEnteredThreeD) {
        this._hasEnteredThreeD = true;
        this.showContextualTip("ctx-3d-mode");
      }
      if (this._hasAddedProduct) {
        this.showProgressTip("prog-3d-preview");
      }
    } else if (mode === "2d" && this._hasEnteredThreeD) {
      this.showContextualTip("ctx-back-to-2d");
    }
  }

  onDrawerStateChanged(state: string): void {
    if (!this.isTipsEnabled()) return;
    if (state === "drawer") {
      this.showContextualTip("ctx-drawer-active");
    }
  }

  onProductSelected(): void {
    if (!this.isTipsEnabled()) return;
    this._scheduleTip(300, () => {
      this.showContextualTip("ctx-product-selected");
    });
  }

  onUnfinishedCorners(count: number): void {
    if (!this.isTipsEnabled()) return;
    if (count > 0) {
      this._scheduleTip(500, () => {
        this.showContextualTip("ctx-corner-buttons");
      });
    }
  }

  onProductAdded(): void {
    if (!this.isTipsEnabled()) return;
    this._hasAddedProduct = true;
    this.showProgressTip("prog-first-product");
  }

  onRoomCreated(): void {
    if (!this.isTipsEnabled()) return;
    this.showProgressTip("prog-room-created");
  }

  onMaterialChanged(): void {
    if (!this.isTipsEnabled()) return;
    this.showProgressTip("prog-material-changed");
  }

  // --- Internal ---

  private _isHighlightable(el: Element | null): el is HTMLElement {
    if (!(el instanceof HTMLElement)) return false;
    const rect = el.getBoundingClientRect();
    return rect.width > 1 && rect.height > 1;
  }

  private _query(selector: string): Element | null {
    if (this._rootElement) {
      const inRoot = this._rootElement.querySelector(selector);
      if (inRoot) return inRoot;
    }
    return document.querySelector(selector);
  }

  private _findElement(selector: string): Element | null {
    // `#view-mode-bar` is `display: contents` (no box). Always target the
    // actual 2D/3D pill — chrome in the top bar, or mobile at the bottom.
    if (
      selector === "#view-mode-toggle" ||
      selector === "#view-mode-bar"
    ) {
      const chrome = this._query("#view-mode-toggle");
      if (this._isHighlightable(chrome)) return chrome;
      const mobile = this._query("#view-mode-toggle-mobile");
      if (this._isHighlightable(mobile)) return mobile;
      return chrome ?? mobile;
    }

    return this._query(selector);
  }

  private _scheduleTip(delayMs: number, fn: () => void): void {
    const timer = setTimeout(() => {
      this._pendingTipTimers.delete(timer);
      if (!this.isTipsEnabled()) return;
      fn();
    }, delayMs);
    this._pendingTipTimers.add(timer);
  }

  private _clearPendingTipTimers(): void {
    for (const timer of this._pendingTipTimers) {
      clearTimeout(timer);
    }
    this._pendingTipTimers.clear();
  }

  private _destroyActive(): void {
    if (this._driverInstance?.isActive()) {
      this._driverInstance.destroy();
    }
    this._driverInstance = null;
  }

  private _clearAutoDismiss(): void {
    if (this._autoDismissTimer) {
      clearTimeout(this._autoDismissTimer);
      this._autoDismissTimer = null;
    }
  }
}
